import * as core from '@actions/core';
import * as exec from '@actions/exec';
import { fencedBlock, truncate } from './utils';

export type AutoMergeResult =
  | { status: 'completed' }
  | { status: 'failed'; exitCode: number | null; diagnostic: string };

export async function enableAutoMerge({
  owner,
  repo,
  prNumber,
  reviewedHeadSha,
  githubToken,
}: {
  owner: string;
  repo: string;
  prNumber: number;
  reviewedHeadSha: string;
  githubToken: string;
}): Promise<AutoMergeResult> {
  const repository = `${owner}/${repo}`;
  let result: Awaited<ReturnType<typeof exec.getExecOutput>>;
  try {
    result = await exec.getExecOutput('gh', [
      'pr',
      'merge',
      String(prNumber),
      '--repo',
      repository,
      '--auto',
      '--squash',
      '--match-head-commit',
      reviewedHeadSha,
    ], {
      env: ghEnvironment(githubToken),
      ignoreReturnCode: true,
      silent: true,
    });
  } catch (error) {
    const diagnostic = truncate(error instanceof Error ? error.message : String(error), 3000);
    core.warning(`Could not run the gh auto-merge request for ${repository}#${prNumber}: ${diagnostic}`);
    return { status: 'failed', exitCode: null, diagnostic };
  }
  if (result.exitCode !== 0) {
    const diagnostic = truncate(
      [result.stderr.trim(), result.stdout.trim()].filter(Boolean).join('\n') || 'gh produced no diagnostic output.',
      3000,
    );
    core.warning(`gh auto-merge request failed for ${repository}#${prNumber} (exit code ${result.exitCode}): ${diagnostic}`);
    return { status: 'failed', exitCode: result.exitCode, diagnostic };
  }
  core.info(`gh squash auto-merge request completed for ${repository}#${prNumber} at ${reviewedHeadSha}.`);
  return { status: 'completed' };
}

export function renderAutoMergeFailureComment({
  result,
  reviewedHeadSha,
}: {
  result: Extract<AutoMergeResult, { status: 'failed' }>;
  reviewedHeadSha: string;
}): string {
  return [
    '## Codex Review Auto-merge',
    '',
    ':x: Codex Review returned `APPROVE`, but the squash auto-merge request failed.',
    '',
    `Reviewed head: \`${reviewedHeadSha}\``,
    `GitHub CLI exit code: \`${result.exitCode === null ? 'unavailable' : result.exitCode}\``,
    '',
    '<details>',
    '<summary>GitHub CLI diagnostic</summary>',
    '',
    fencedBlock('text', result.diagnostic),
    '',
    '</details>',
    '',
    'The review remains `APPROVE`; this auto-merge error did not fail the Codex Review step.',
  ].join('\n');
}

function ghEnvironment(githubToken: string): { [key: string]: string } {
  const env: { [key: string]: string } = {};
  for (const [key, value] of Object.entries(process.env)) {
    if (value !== undefined) {
      env[key] = value;
    }
  }
  env.GH_TOKEN = githubToken;
  env.GH_PROMPT_DISABLED = '1';
  return env;
}
